'use client';

import { useCallback, useState } from 'react';

import { recordEnquiryReveal } from '@/features/enquiry/api';

interface EmailRevealProps {
  email: string;
  /** Classes applied to the "Show email" reveal button (hidden state). */
  hiddenClassName?: string;
  /** Classes applied to the email button (revealed state). */
  shownClassName?: string;
  /** Associated listing ID if revealed within a listing context. */
  listingId?: number | string;
  /** Optional metadata associated with the reveal interaction. */
  metadata?: Record<string, unknown>;
  /** Optional callback fired when reveal is triggered. */
  onReveal?: () => void;
}

const gmailComposeUrl = (email: string) =>
  `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(email)}`;

/**
 * Email reveal control that prefers `mailto:` when the browser can
 * route it, falling back to Gmail web compose in a new tab only when no
 * mail handler is registered.
 *
 * Behavior matrix:
 *
 *   - Desktop mail client registered (Thunderbird, Outlook desktop,
 *     Apple Mail, …)  → that client opens. No new tab.
 *   - Chrome / Edge has Gmail (or another provider) registered as the
 *     `mailto:` handler via `navigator.registerProtocolHandler` →
 *     that provider opens. No new tab.
 *   - Nothing registered  → 500ms grace period; if the page still has
 *     focus (no handler fired), a single new tab opens Gmail web
 *     compose. Exactly one new tab.
 *
 * No popover, no popup chain, no UI choice — the design is unchanged
 * at rest.
 */
export function EmailReveal({
  email,
  hiddenClassName,
  shownClassName,
  listingId,
  metadata,
  onReveal,
}: EmailRevealProps) {
  const [revealed, setRevealed] = useState(false);

  const handleClick = useCallback(() => {
    if (!revealed) {
      setRevealed(true);
      onReveal?.();

      const numericListingId: number | undefined = (() => {
        if (typeof listingId === 'number' && Number.isFinite(listingId) && listingId > 0) {
          return Math.floor(listingId);
        }
        if (typeof listingId === 'string') {
          const trimmed = listingId.trim();
          const directNum = Number(trimmed);
          if (Number.isFinite(directNum) && directNum > 0) {
            return Math.floor(directNum);
          }
          const digits = trimmed.replace(/\D/g, '');
          if (digits) {
            const parsed = parseInt(digits, 10);
            if (Number.isFinite(parsed) && parsed > 0) {
              return parsed;
            }
          }
        }
        return undefined;
      })();

      void recordEnquiryReveal({
        source: 'EMAIL_REVEAL',
        ...(numericListingId !== undefined ? { listingId: numericListingId } : {}),
        metadata: {
          page: typeof window !== 'undefined' ? window.location.pathname : undefined,
          ...(listingId !== undefined ? { listingId: String(listingId) } : {}),
          ...metadata,
        },
      });
      return;
    }

    // 1. Primary path: hand off to the OS / browser-registered mail
    //    handler. This is what the user asked for when their browser
    //    has any email account logged in and configured.
    window.location.href = `mailto:${email}`;

    // 2. If no handler claims the navigation within 500ms the page
    //    retains focus; in that case open exactly one Gmail web
    //    compose tab as the documented fallback. The grace period is
    //    short on purpose: the user should not wait long.
    const fallback = window.setTimeout(() => {
      if (typeof document !== 'undefined' && document.hasFocus()) {
        window.open(gmailComposeUrl(email), '_blank', 'noopener,noreferrer');
      }
    }, 500);

    return () => window.clearTimeout(fallback);
  }, [revealed, email, listingId, metadata, onReveal]);

  if (!revealed) {
    return (
      <button
        type="button"
        onClick={handleClick}
        className={hiddenClassName ?? 'cursor-pointer underline transition hover:text-white'}
      >
        Show email
      </button>
    );
  }

  return (
    <button type="button" onClick={handleClick} className={shownClassName}>
      {email}
    </button>
  );
}
